import React, { useEffect, useState } from 'react';
import { Review } from '../types/Review';
import { reviewService } from '../services/reviewService';
import '../styles/DashboardSummary.css';

interface DashboardSummaryProps {
  reviews: Review[];
}

interface DashboardStats {
  totalReviews: number;
  totalHighlights: number;
  reviewsWithHighlights: number;
  avgRating: number;
  avgImpactScore: number;
  extractionRate: number;
  transformations: number;
  professionals: number;
  byPriority: {
    GOLD: number;
    SILVER: number;
    BRONZE: number;
  };
}

export const DashboardSummary: React.FC<DashboardSummaryProps> = ({ reviews }) => {
  const [stats, setStats] = useState<DashboardStats | null>(null);
  const [loading, setLoading] = useState(true);
  
  useEffect(() => {
    loadStats();
  }, []);
  
  const loadStats = async () => {
    try {
      const statsData = await reviewService.getStats();
      setStats(statsData as any);
    } catch (error) {
      console.error('Failed to load stats:', error);
      // Fallback to calculating from current reviews
      calculateLocalStats();
    } finally {
      setLoading(false);
    }
  };
  
  const calculateLocalStats = () => {
    // Fallback calculation from local reviews
    const totalReviews = reviews.length;
    const withHighlights = reviews.filter(r => r.highlights && r.highlights.length > 0).length;
    const totalHighlights = reviews.reduce((sum, r) => sum + (r.highlights?.length || 0), 0);
    const avgRating = reviews.length > 0
      ? (reviews.reduce((sum, r) => sum + (r.rating || r.originalRating || 0), 0) / reviews.length)
      : 0;
    const avgImpactScore = reviews.length > 0
      ? Math.round(reviews.reduce((sum, r) => sum + (r.metadata?.impact_score || 0), 0) / reviews.length)
      : 0;
    
    setStats({
      totalReviews,
      totalHighlights,
      reviewsWithHighlights: withHighlights,
      avgRating,
      avgImpactScore,
      extractionRate: totalReviews > 0 ? (withHighlights / totalReviews) * 100 : 0,
      transformations: reviews.filter(r => r.metadata?.has_transformation || r.transformation_story).length,
      professionals: reviews.filter(r => r.metadata?.profession).length,
      byPriority: {
        GOLD: reviews.filter(r => r.metadata?.priority === 'GOLD').length,
        SILVER: reviews.filter(r => r.metadata?.priority === 'SILVER').length,
        BRONZE: reviews.filter(r => r.metadata?.priority === 'BRONZE').length,
      }
    });
  };
  
  // Use stats from API or loading state
  const totalReviews = stats?.totalReviews || 0;
  const totalHighlights = stats?.totalHighlights || 0;
  const withHighlights = stats?.reviewsWithHighlights || 0;
  const avgRating = stats?.avgRating?.toFixed(1) || '0.0';
  const avgImpactScore = Math.round(stats?.avgImpactScore || 0);
  const extractionRate = stats?.extractionRate || 0;
  const transformations = stats?.transformations || 0;
  const professionals = stats?.professionals || 0;
  const priorityCounts = {
    gold: stats?.byPriority?.GOLD || 0,
    silver: stats?.byPriority?.SILVER || 0,
    bronze: stats?.byPriority?.BRONZE || 0,
  };
  const hasPriorityReviews = priorityCounts.gold > 0 || priorityCounts.silver > 0 || priorityCounts.bronze > 0;

  return (
    <div className="dashboard-summary">
      {/* Primary Metrics Row */}
      <div className="primary-metrics">
        <div className="metric-item primary">
          <div className="metric-value">{totalReviews.toLocaleString()}</div>
          <div className="metric-label">Total Reviews</div>
        </div>
        <div className="metric-divider"></div>
        <div className="metric-item">
          <div className="metric-value">{totalHighlights.toLocaleString()}</div>
          <div className="metric-label">Marketing Highlights</div>
        </div>
        <div className="metric-divider"></div>
        <div className="metric-item">
          <div className="metric-value">{avgRating}</div>
          <div className="metric-label">Average Rating</div>
        </div>
        <div className="metric-divider"></div>
        <div className="metric-item">
          <div className="metric-value">
            {avgImpactScore}
            <span className="metric-suffix">%</span>
          </div>
          <div className="metric-label">Impact Score</div>
        </div>
      </div>

      {/* Secondary Metrics Bar */}
      <div className="secondary-metrics">
        <div className="metrics-row">
          {/* Extraction Rate */}
          <div className="metric-card">
            <div className="metric-icon extraction"></div>
            <div className="metric-content">
              <div className="metric-title">Extraction Rate</div>
              <div className="metric-detail">
                <span className="metric-number">{withHighlights}</span>
                <span className="metric-context">of {totalReviews} reviews</span>
                <span className="metric-percent">({Math.round(extractionRate)}%)</span>
              </div>
            </div>
          </div>

          {/* Priority Distribution - Only show if there are priority reviews */}
          {hasPriorityReviews && (
            <div className="metric-card priority-card">
              <div className="metric-icon priority"></div>
              <div className="metric-content">
                <div className="metric-title">Priority Reviews</div>
                <div className="priority-bars">
                  {priorityCounts.gold > 0 && (
                    <div className="priority-bar gold" style={{width: `${(priorityCounts.gold / (priorityCounts.gold + priorityCounts.silver + priorityCounts.bronze)) * 100}%`}}>
                      <span className="priority-value">{priorityCounts.gold}</span>
                    </div>
                  )}
                  {priorityCounts.silver > 0 && (
                    <div className="priority-bar silver" style={{width: `${(priorityCounts.silver / (priorityCounts.gold + priorityCounts.silver + priorityCounts.bronze)) * 100}%`}}>
                      <span className="priority-value">{priorityCounts.silver}</span>
                    </div>
                  )}
                  {priorityCounts.bronze > 0 && (
                    <div className="priority-bar bronze" style={{width: `${(priorityCounts.bronze / (priorityCounts.gold + priorityCounts.silver + priorityCounts.bronze)) * 100}%`}}>
                      <span className="priority-value">{priorityCounts.bronze}</span>
                    </div>
                  )}
                </div>
              </div>
            </div>
          )}

          {/* Social Proof - Only show if there are transformations or professionals */}
          {(transformations > 0 || professionals > 0) && (
            <div className="metric-card">
              <div className="metric-icon social"></div>
              <div className="metric-content">
                <div className="metric-title">Social Proof</div>
                <div className="metric-detail">
                  {transformations > 0 && (
                    <span className="proof-item">
                      <strong>{transformations}</strong> transformations
                    </span>
                  )}
                  {transformations > 0 && professionals > 0 && <span className="separator">•</span>}
                  {professionals > 0 && (
                    <span className="proof-item">
                      <strong>{professionals}</strong> professionals
                    </span>
                  )}
                </div>
              </div>
            </div>
          )}
        </div>
      </div>
      
      {/* Status Indicators - Only show if there are insights */}
      {(avgImpactScore > 60 || transformations > 10 || professionals > 5 || (withHighlights < totalReviews * 0.3 && totalReviews > 10)) && (
        <div className="status-indicators">
          {avgImpactScore > 60 && (
            <div className="status-item success">
              <div className="status-icon"></div>
              <span>High marketing potential</span>
            </div>
          )}
          {transformations > 10 && (
            <div className="status-item success">
              <div className="status-icon"></div>
              <span>{transformations} transformation stories</span>
            </div>
          )}
          {professionals > 5 && (
            <div className="status-item info">
              <div className="status-icon"></div>
              <span>{professionals} professional endorsements</span>
            </div>
          )}
          {withHighlights < totalReviews * 0.3 && totalReviews > 10 && (
            <div className="status-item warning">
              <div className="status-icon"></div>
              <span>Low extraction rate - check settings</span>
            </div>
          )}
        </div>
      )}
    </div>
  );
};

export default DashboardSummary;